/**
 * Agent Form Injector
 * 
 * Provides a fallback mechanism to inject context question forms when
 * the AI agent forgets to include them in responses about platform actions.
 * 
 * This ensures users always get the interactive form for adding suppliers,
 * creating Actions, etc., even if the AI doesn't include the marker.
 */

import { ContextQuestion } from '@/components/agent/ContextQuestionForm';

interface InjectionResult {
  content: string;
  injectedQuestion: ContextQuestion | null;
  wasInjected: boolean;
}

interface MessageContext {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

// Pattern to detect when AI is offering to add a marketplace supplier but missing the form
const ADD_SUPPLIER_PATTERNS = [
  /would you like (?:me )?to add (?:them|it|this supplier) to your suppliers\??/i,
  /select your preferences:?\s*$/i,
  /to add them to your suppliers/i,
  /I can add (?:them|it|this) to your suppliers/i,
  /add (?:them|it) to your (?:supplier|organisation)/i,
];

// Pattern to detect when AI found a supplier with an ID
const MARKETPLACE_ID_PATTERN = /marketplace_listing_id[:\s]+["']?([a-f0-9-]{36})["']?/i;
const FOUND_IN_MARKETPLACE_PATTERN = /found\s+\*?\*?([^*]+?)\*?\*?\s+in\s+(?:the\s+)?marketplace/i;

// UUID pattern to extract listing IDs from message content
const UUID_PATTERN = /\b([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})\b/gi;

// Pattern to detect new supplier request without form
const NEW_SUPPLIER_PATTERNS = [
  /couldn't find .+ in (?:your organisation|the marketplace)/i,
  /not (?:found|available) in the marketplace/i,
  /I'll need (?:their|the) (?:website )?domain/i,
];

// Pattern to detect Action creation offer without form
const CREATE_POAM_PATTERNS = [
  /create a (?:new )?po&?m/i,
  /I'll create (?:a|the) (?:new )?po&?m/i,
  /let me create a risk (?:action|record)/i,
  // ENT-1633 Issue 13: after the PO&M -> Actions rename, the assistant's copy
  // says "create a/an/the Action" instead of "PO&M". Match the renamed phrasing so
  // the create-form still injects when no structured create_poam_context marker is present.
  /create (?:a|an|the) (?:new )?action\b/i,
];

/**
 * Extract marketplace listing ID from conversation history.
 * Looks for recent messages where the agent found a supplier in the marketplace.
 */
export function extractListingIdFromHistory(
  messages: MessageContext[],
  companyName?: string
): { id: string; name: string } | null {
  // Search from most recent to oldest
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role !== 'assistant') continue;
    
    const content = msg.content;
    
    // Look for explicit marketplace_listing_id mentions
    const idMatch = content.match(MARKETPLACE_ID_PATTERN);
    if (idMatch) {
      const nameMatch = content.match(FOUND_IN_MARKETPLACE_PATTERN);
      return {
        id: idMatch[1],
        name: nameMatch ? nameMatch[1].trim() : companyName || 'this supplier',
      };
    }
    
    // Look for structured data with marketplace IDs in the message
    // This pattern matches when the agent might have displayed listing details
    const structuredMatch = content.match(/id['":\s]+([a-f0-9-]{36})/i);
    if (structuredMatch && content.toLowerCase().includes('marketplace')) {
      const nameMatch = content.match(FOUND_IN_MARKETPLACE_PATTERN) || 
                        content.match(/\*\*([^*]+)\*\*/); // Bold company name
      return {
        id: structuredMatch[1],
        name: nameMatch ? (nameMatch[1] || '').trim() : companyName || 'this supplier',
      };
    }
    
    // Check for UUIDs that appear near "marketplace" or "found" keywords
    if (content.toLowerCase().includes('found') && content.toLowerCase().includes('marketplace')) {
      const uuids = content.match(UUID_PATTERN);
      if (uuids && uuids.length > 0) {
        const nameMatch = content.match(FOUND_IN_MARKETPLACE_PATTERN) ||
                          content.match(/\*\*([^*]+)\*\*/);
        return {
          id: uuids[0],
          name: nameMatch ? (nameMatch[1] || '').trim() : companyName || 'this supplier',
        };
      }
    }
  }
  
  return null;
}

/**
 * Check if the message content appears to be offering a platform action
 * but is missing the required CONTEXT_QUESTION form.
 * 
 * @param content - The current message content
 * @param hasContextQuestion - Whether a context question was already parsed
 * @param messageHistory - Previous messages for context extraction
 */
export function detectMissingForm(
  content: string,
  hasContextQuestion: boolean,
  messageHistory?: MessageContext[]
): InjectionResult {
  // If there's already a context question, no injection needed
  if (hasContextQuestion) {
    return { content, injectedQuestion: null, wasInjected: false };
  }

  // Check for add supplier pattern
  for (const pattern of ADD_SUPPLIER_PATTERNS) {
    if (pattern.test(content)) {
      // Try to extract company name from the message
      const nameMatch = content.match(FOUND_IN_MARKETPLACE_PATTERN);
      const companyName = nameMatch ? nameMatch[1].trim() : 'this supplier';
      
      // Try to extract marketplace_listing_id from current message
      const idMatch = content.match(MARKETPLACE_ID_PATTERN);
      
      // If not found, try to extract from message history
      if (!idMatch && messageHistory && messageHistory.length > 0) {
        const historyResult = extractListingIdFromHistory(messageHistory, companyName);
        if (historyResult) {
          // We have the ID from history, inject the form
          const question: ContextQuestion = {
            id: 'add_supplier_context',
            hidden_data: {
              marketplace_listing_id: historyResult.id,
              company_name: historyResult.name,
            },
            fields: [
              {
                id: 'criticality_tier',
                label: 'Criticality Tier',
                type: 'select',
                options: [
                  { value: 'critical', label: 'Critical' },
                  { value: 'high', label: 'High' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'low', label: 'Low' },
                ],
                required: true,
              },
              {
                id: 'supplier_status',
                label: 'Supplier Status',
                type: 'select',
                options: [
                  { value: 'active', label: 'Active' },
                  { value: 'onboarding', label: 'Onboarding' },
                ],
                required: true,
              },
            ],
          };
          
          return { content, injectedQuestion: question, wasInjected: true };
        }
      }
      
      // Check if ID is directly in current message
      if (idMatch) {
        // We have the ID, inject the form
        const question: ContextQuestion = {
          id: 'add_supplier_context',
          hidden_data: {
            marketplace_listing_id: idMatch[1],
            company_name: companyName,
          },
          fields: [
            {
              id: 'criticality_tier',
              label: 'Criticality Tier',
              type: 'select',
              options: [
                { value: 'critical', label: 'Critical' },
                { value: 'high', label: 'High' },
                { value: 'medium', label: 'Medium' },
                { value: 'low', label: 'Low' },
              ],
              required: true,
            },
            {
              id: 'supplier_status',
              label: 'Supplier Status',
              type: 'select',
              options: [
                { value: 'active', label: 'Active' },
                { value: 'onboarding', label: 'Onboarding' },
              ],
              required: true,
            },
          ],
        };
        
        return { content, injectedQuestion: question, wasInjected: true };
      }
      
      // No ID found anywhere - this is a bug in the agent
      // Don't show an error message, just log and skip injection
      // The user can try the request again
      console.warn('[AgentFormInjector] Could not extract marketplace_listing_id for add supplier action');
      return { content, injectedQuestion: null, wasInjected: false };
    }
  }

  // Check for new supplier request pattern
  for (const pattern of NEW_SUPPLIER_PATTERNS) {
    if (pattern.test(content)) {
      // Extract mentioned company name
      const nameMatch = content.match(/(?:find|add|couldn't find)\s+["']?([^"',.]+?)["']?\s+(?:in|to|as)/i);
      const companyName = nameMatch ? nameMatch[1].trim() : 'the supplier';
      
      const question: ContextQuestion = {
        id: 'request_supplier_context',
        hidden_data: {
          company_name: companyName,
        },
        fields: [
          {
            id: 'domain_or_email',
            label: 'Domain or Email Address',
            type: 'text',
            placeholder: 'e.g. example.com or contact@example.com',
            required: true,
          },
        ],
      };
      
      return { content, injectedQuestion: question, wasInjected: true };
    }
  }

  // Check for Action creation pattern
  for (const pattern of CREATE_POAM_PATTERNS) {
    if (pattern.test(content)) {
      const question: ContextQuestion = {
        id: 'create_poam_context',
        fields: [
          {
            id: 'title',
            label: 'Title',
            type: 'text',
            placeholder: 'Brief description of the risk or action',
            required: true,
          },
          {
            id: 'severity',
            label: 'Severity',
            type: 'select',
            options: [
              { value: 'critical', label: 'Critical' },
              { value: 'high', label: 'High' },
              { value: 'medium', label: 'Medium' },
              { value: 'low', label: 'Low' },
            ],
            required: true,
          },
          {
            id: 'due_date',
            label: 'Due Date',
            type: 'date',
            required: false,
          },
        ],
      };
      
      return { content, injectedQuestion: question, wasInjected: true };
    }
  }

  return { content, injectedQuestion: null, wasInjected: false };
}
